import SeekerStore from "../stores/seekerStore";
import { action } from "mobx";
import {
  Profile, SavedOpportunity, SkillFit, WorkExperience, EducationExperience, ActivityExperience,
  CCLCExperience, ExperienceSkill, IExperience, InitSkillLevel
} from "../models/profile";
import { Opportunity, SkillSet, SetSkill, SimFactor, Prereq, PrereqField, CCLCReq, LearnedSkill, CompanyLevels } from "../models/opportunity";
import { OperatorType, PrereqType, EmptyESId, BusyEvent } from "../models/enums";
import Fetcher from "./fetcher";

export default class ScoringEngine {
  private _store: SeekerStore;
  constructor(store: SeekerStore) {
    this._store = store;
  }

  rescoreAll() {
    const profile: Profile = this._store.profileStore.profile;
    if (!profile) {
      return;
    }
    profile.savedOps.map((so: SavedOpportunity, i: number): void => {
      const op: Opportunity = this._store.opportunityStore.getOpportunity(so.opportunityId);
      if (op) {
        this.scoreOpportunity(op, so);
      }
    });
  }
  rescore = (skillId: string): void => {
    const profile: Profile = this._store.profileStore.profile;
    if (!profile) {
      return;
    }
    profile.savedOps.map((so: SavedOpportunity, i: number): void => {
      const op: Opportunity = this._store.opportunityStore.getOpportunity(so.opportunityId);
      if (op && op.hasSkill(skillId)) {
        this.scoreOpportunity(op, so, skillId);
      }
    });
  }
  @action scoreOpportunity(op: Opportunity, so: SavedOpportunity, skillId?: string): number {
    if (!op || !op.skillSets || op.skillSets.length === 0) {
      return 0;
    }
    this._store.viewState.setBusyBit(BusyEvent.ScoreOpportunities);
    let skills: SetSkill[] = [];
    if (skillId) {
      skills = op.skillSets[0].skills.filter((ss: SetSkill): boolean => {
        return ss.skillId === skillId;
      });
    } else {
      skills = op.skillSets[0].skills;
    }
    skills.map((sk: SetSkill, i: number): void => {
      let sf: SimFactor = op.simFactors.find((s: SimFactor): boolean => {
        return s.idHash === sk.factorId;
      });
      if (!sf && this._store.templates) {
        sf = this._store.templates.factors;
      }
      const lvl: number = sf ? this.calcScore(op, sf, sk) : 0;
      let fit: SkillFit = so.skillFit.find((f: SkillFit): boolean => {
        return f.skill === sk.skillId;
      });
      if (!fit) {
        fit = new SkillFit({ skill: sk.skillId, level: lvl, viewed: lvl > 0 });
        so.skillFit.push(fit);
      } else {
        fit.level = lvl;
        fit.viewed = lvl > 0;
      }
    });
    this._store.viewState.clearBusyBit(BusyEvent.ScoreOpportunities);
    return this.calcFit(op, so);
  }
  private getDate(mon: number, yr: number, dt: Date): Date {
    let dt2: Date = new Date(yr, mon - 1, 1);
    dt2 = new Date(dt2.getTime() - (24 * 60 * 60 * 1000));
    return dt2 < dt ? dt2 : dt;
  }
  private calcScore(op: Opportunity, sf: SimFactor, sk: SetSkill): number {
    const profile: Profile = this._store.profileStore.profile;
    if (!profile) {
      return 0;
    }
    // Get the date ranges for simulation and initialize local storage used to accumuate results.
    let from: Date = new Date();
    let to: Date = new Date();
    profile.jobs.map((we: WorkExperience): void => {
      from = this.getDate(we.fromMon, we.fromYear, from);
    });
    profile.educations.map((ee: EducationExperience): void => {
      from = this.getDate(ee.fromMon, ee.fromYear, from);
    });
    profile.activities.map((ae: ActivityExperience): void => {
      if (ae.actType === 1) {
        from = this.getDate(ae.fromMon, ae.fromYear, from);
      } else if (ae.actType === 2 && ae.toYear !== 0) {
        from = this.getDate(ae.toMon, ae.toYear, from);
      }
    });
    profile.cclcs.map((ce: CCLCExperience): void => {
      if (ce.received) {
        from = this.getDate(ce.received.getMonth() + 1, ce.received.getFullYear(), from);
      } else if (ce.toYear && ce.toMon && ce.toYear >= 1900 && ce.toMon >= -1) {
        from = this.getDate(ce.toMon, ce.toYear, from);
      }
    });
    const mon: number = ((to.getFullYear() - from.getFullYear()) * 12) + to.getMonth() - from.getMonth();
    let useFreq: number[] = [];
    let injection: number[] = [];
    let disc: number[] = [];
    useFreq.length = mon;
    injection.length = mon;
    disc.length = mon;
    for (let i: number = 0; i < mon; ++i) {
      useFreq[i] = 0;
      injection[i] = 0;
      disc[i] = 1.0;
    }

    // Next, set up our usage frequency data based on experience.
    profile.jobs.map((we: WorkExperience): void => {
      this.updateJobUseFrequency(op, we, useFreq, injection, disc, mon, from, to, sk.skillId);
    });
    profile.activities.map((ae: ActivityExperience): void => {
      this.updateActivityUseFrequency(op, ae, useFreq, injection, disc, mon, from, to, sk.skillId);
    });
    profile.cclcs.map((c: CCLCExperience): void => {
      this.updateCCLCUseFrequency(op, c, useFreq, injection, disc, mon, from, to, sk.skillId);
    });
    profile.educations.map((ee: EducationExperience): void => {
      this.updateEducationUseFrequency(op, ee, useFreq, injection, disc, mon, from, to, sk.skillId);
    });
    // Now, perform our analysis
    if (useFreq.length === 0 || injection.length === 0) {
      return 0;
    }
    if (useFreq.reduce((a, c) => a + c) === 0 && injection.reduce((a, c) => a + c) === 0) {
      return 0;
    }
    const l: number[] = [1, 0, 0, 0, 0];
    const pr: number[] = [0, 0, 0, 0];
    const rr: number[] = [0, 0, 0, 0];
    let al: number = 0.0;
    let cmon: number = 0;
    const DT: number = 0.25;
    let start: number = -1;
    for (let i: number = 0; i < injection.length; ++i) {
      if (injection[i] > 0) {
        start = i;
        cmon = i;
        break;
      }
    }
    for (let t: number = start; t < mon; t += DT) {
      var inject = t === (t | 0);
      if (inject && (t == start || injection[cmon] > al)) {
        for (var i = 0; i < 5; ++i)
          l[i] = injection[cmon] == (i + 1) ? 1.0 : 0.0;
      }
      var usage = useFreq[cmon];
      for (var i = 0; i < 4; ++i) {
        var ufm = usage > 0 ? sf.progMult[i][usage - 1] : 0.0;
        var udv = ufm > 0 ? ((sf.progNorm[i] * 12.0) / ufm) : 0.0;
        var rfm = usage > 0 ? sf.regMult[i][usage - 1] : 1.0; // If usage == 0, then rfm = 1 to ensure we are getting decay for the given level
        var rdv = rfm > 0 ? ((sf.regNorm[i] * 12.0) / rfm) : 0.0;
        pr[i] = udv > 0 ? l[i] / udv : 0.0;
        pr[i] *= disc[i];
        rr[i] = rdv > 0 ? l[i + 1] / rdv : 0.0;
      }
      l[0] += (-pr[0] + rr[0]) * DT;
      for (var i = 1; i <= 3; ++i)
        l[i] += (pr[i - 1] - pr[i] - rr[i - 1] + rr[i]) * DT;
      l[4] += (pr[3] - rr[3]) * DT;

      al = 0.0;
      for (var i = 0; i < 5; ++i) {
        al += l[i] * (i + 1);
      }
      if (inject && t > 0) {
        cmon++;
      }
    }
    return al;
  }
  private updateEducationUseFrequency(op: Opportunity, src: EducationExperience, useFreq: number[], injection: number[], disc: number[], mon: number, from: Date, to: Date, skillId: string): void {
    var used = src.skills.find((es: ExperienceSkill): boolean => {
      return es.skillId === skillId;
    });
    if (used) {
      var l = [0, 0, 0, 0, 0];
      if (src.edType == 1) {
        if (src.grad && used.used > 0) {
          let dt: Date = new Date(src.toYear, src.toMon - 1, 1);
          if (dt > to) {
            dt = to;
          }
          var offset = ((dt.getFullYear() - from.getFullYear()) * 12) + dt.getMonth() - from.getMonth();
          offset = Math.min(mon - 1, offset);
          switch (src.degTypeId) {
            case 1:
              l = [0, 2, 1, 0]; break;
            case 2:
              l = [0, 2, 1, 1]; break;
            case 3:
              l = [0, 3, 2, 1]; break;
            case 4:
              l = [0, 4, 3, 2]; break;
            case 5:
              l = [0, 5, 4, 2]; break;
          }
          injection[offset] = Math.max(injection[offset], l[used.used]);
        }
      }
      else if (src.edType == 2) {
        if (!src.active && src.passed && src.toYear > 0 && src.toMon > 0) {
          let dt: Date = new Date(src.toYear, src.toMon - 1, 1);
          if (dt > to) {
            dt = to;
          }
          var offset = ((dt.getFullYear() - from.getFullYear()) * 12) + dt.getMonth() - from.getMonth();
          offset--;
          offset = Math.min(mon - 1, offset);
          injection[offset] = Math.max(injection[offset], src.level);
        }
      }
    }
  }
  private getRefCclc(id: string): CCLCReq {
    let cclc: CCLCReq = null;
    this._store.opportunityStore.opportunities.map((op: Opportunity): void => {
      if (cclc !== null) {
        return;
      }
      op.prereqs.map((pr: Prereq): void => {
        if (cclc !== null) {
          return;
        }
        pr.fields.map((fld: PrereqField): void => {
          if (fld.opType === OperatorType.FIELD && fld.type === PrereqType.CCLC && fld.refVal === id) {
            cclc = fld.refCclc;
          }
        });
      });
    });
    return cclc;
  }
  private updateCCLCUseFrequency(op: Opportunity, src: CCLCExperience, useFreq: number[], injection: number[], disc: number[], mon: number, from: Date, to: Date, skillId: string): void {
    var refCclc = this.getRefCclc(src.cclcId);
    if (refCclc != null) {
      var used = refCclc.skills.find((ls: LearnedSkill): boolean => {
        return ls.skillId === skillId;
      });
      if (used && src.received) {
        var refOrgId = op.orgIdHash;
        var orgSkill = used.orgSkills.find((cl: CompanyLevels): boolean => {
          return cl.orgId === refOrgId && cl.levels && cl.levels.length > 0;
        });
        if (!orgSkill) {
          orgSkill = used.orgSkills.find((cl: CompanyLevels): boolean => {
            return cl.default;
          });
        }
        if (orgSkill != null && orgSkill.levels && orgSkill.levels && orgSkill.levels.length > 0) {
          let dt: Date = new Date(src.fromYear, src.fromMon - 1, 1);
          if (dt > to)
            dt = to;
          var offset = ((dt.getFullYear() - from.getFullYear()) * 12) + dt.getMonth() - from.getMonth();
          injection[offset] = Math.max(injection[offset], orgSkill.levels[0].level);
        }
      }
    }
  }
  private updateActivityUseFrequency(op: Opportunity, src: ActivityExperience, useFreq: number[], injection: number[], disc: number[], mon: number, from: Date, to: Date, skillId: string) {
    // If this is a scalar activity, set level and bail
    const used: ExperienceSkill = src.skills.find((es: ExperienceSkill): boolean => { return es.skillId === skillId });
    if (used && src.activityType === 'activities' && src.actType === 2) {
      let dt: Date = new Date(src.toYear, src.toMon - 1, 1);
      if (dt > to) {
        dt = to;
      }
      var offset = ((dt.getFullYear() - from.getFullYear()) * 12) + dt.getMonth() - from.getMonth();
      offset = Math.min(mon - 1, offset);
      injection[offset] = 2;
      return;
    }
    if (used && used.used > 0) {
      let dt: Date = new Date(src.fromYear, src.fromMon - 1, 1); //op.getDate(src.fromMon(), src.fromYear(), to);
      var fromOffset = ((dt.getFullYear() - from.getFullYear()) * 12) + dt.getMonth() - from.getMonth();
      if (injection[fromOffset] == 0)
        injection[fromOffset] = 1; // Start our skill
      dt = src.active ? to : new Date(src.toYear, src.toMon - 1, 1);
      var toOffset = ((dt.getFullYear() - from.getFullYear()) * 12) + dt.getMonth() - from.getMonth();
      toOffset = Math.min(mon - 1, toOffset);
      for (var i = fromOffset; i <= toOffset; ++i) {
        if (useFreq[i] == 0)
          useFreq[i] = used.used;
        else
          useFreq[i] = Math.min(useFreq[i], used.used);
      }
    }
  }
  private updateJobUseFrequency(op: Opportunity, src: WorkExperience, useFreq: number[], injection: number[], disc: number[], mon: number, from: Date, to: Date, skillId: string) {
    const isl:InitSkillLevel = this._store.profileStore.profile.initSkillLevels.find((sl: InitSkillLevel): boolean => {
      return sl.skillId === skillId;
    });
    const initCompetence: number = isl ? isl.initCompetence : 1;;
    // If this is a scalar activity, set level and bail
    const used: ExperienceSkill = src.skills.find((es: ExperienceSkill): boolean => { return es.skillId === skillId });
    if (used && used.used > 0) {
      let dt: Date = new Date(src.fromYear, src.fromMon - 1, 1);
      var fromOffset = ((dt.getFullYear() - from.getFullYear()) * 12) + dt.getMonth() - from.getMonth();
      injection[fromOffset] = Math.max(injection[fromOffset], initCompetence);
      //if (injection[fromOffset] == 0)
      //  injection[fromOffset] = 1; // Start our skill
      dt = src.active ? to : new Date(src.toYear, src.toMon - 1, 1);
      var toOffset = ((dt.getFullYear() - from.getFullYear()) * 12) + dt.getMonth() - from.getMonth();
      toOffset = Math.min(mon - 1, toOffset);
      for (var i = fromOffset; i <= toOffset; ++i) {
        if (useFreq[i] == 0)
          useFreq[i] = used.used;
        else
          useFreq[i] = Math.min(useFreq[i], used.used);
      }
    }
  }
  private calcFit(op: Opportunity, so: SavedOpportunity): number {
    let es: number = 0;
    let us: number = 0;
    op.skillSets.map((set: SkillSet): void => {
      set.skills.map((ss: SetSkill): void => {
        es += (ss.level * ss.mult);
        let fit: SkillFit = so.skillFit.find((sf: SkillFit): boolean => {
          return sf.skill === ss.skillId;
        });
        if (fit) {
          let l: number = fit.level;
          l = Math.min(ss.level, l);
          us += (l * ss.mult);
        }
      });
    });
    let tf: number = es > 0 ? (100 * (us / es)) : 0.0;
    so.fit = Math.round(tf);
    Fetcher.post(`/api/profile/savedop/${op.idHash}`, { fit: so.fit, skillFit: so.skillFit }, () => { });
    return so.fit;
  }
}


// WEBPACK FOOTER //
// ./src/utils/scoring.ts